import React from 'react'
import styled from '@emotion/styled'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { filterSingleItemsForWorkflow } from '@mobi/betslip/Components/Singles/helpers/filterSingleItemsForWorkflow'
import { selectBetSlipItems } from '@mobi/betslip/Store/Bets/selectors'
import { setShouldAllowPendingBetShare } from '@mobi/betslip/Store/Config'
import { BetSlipWorkflowState } from '@mobi/betslip/Store/Workflow'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'
import { getBetsInMulti, isValidMulti } from '@mobi/betslip/helpers/state'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Badge } from '@mobi/component-library/Feedback/Badge'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { AppRoutes } from '@core/AppRoutes'
import { BalancePill } from '@core/Areas/AppHeader/Components/Balance/BalancePill'
import { MyBetsButtons } from '@core/Areas/AppHeader/Components/MyBetsButtons'
import { setBetSlipIsOpen } from '@core/Areas/AppHeader/Store'
import { showLogin } from '@core/Areas/Login/helpers'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { useLogon } from '@core/Utils/hooks'
import { appHistory } from '@core/Utils/routing/history'

const enum LocalConstants {
  CondensedHeaderClass = 'js-header-panel--condensed',
}

export const HeaderPanel: React.FC = () => {
  const dispatch = useAppDispatch()

  const { isLoggedIn, accountNumber } = useLogon()

  React.useEffect(() => {
    if (shouldAllowPendingSharesForAccount(accountNumber)) {
      dispatch(setShouldAllowPendingBetShare())
    }
  }, [accountNumber, dispatch])

  const [isLargeWidth] = React.useState(() => window.innerWidth > 480)

  const currentWorkflowStatus = useAppSelector(selectWorkflowStatus)
  const betSlipItems = useAppSelector(selectBetSlipItems)
  const multiInvestment = useAppSelector(state => state.betslip.bets.multiInvestment)
  const multiBetError = useAppSelector(state => state.betslip.bets.multiBetError)

  const selectedMultiItems = getBetsInMulti(betSlipItems)
  const isMultiValid = isValidMulti(multiInvestment, multiBetError, selectedMultiItems)

  const singleItems = filterSingleItemsForWorkflow(betSlipItems, currentWorkflowStatus)
  const betslipCount = singleItems.length + (isMultiValid ? 1 : 0)

  const isEmptyBetSlip = betSlipItems.length === 0

  const headerText = getWorkflowHeader(currentWorkflowStatus)

  return (
    <HeaderPanelWrapperStyled
      className={!isEmptyBetSlip ? LocalConstants.CondensedHeaderClass : ''}
    >
      <div>
        <h2>
          <button onClick={() => dispatch(setBetSlipIsOpen(false))} type='button'>
            <Icon
              name={isLargeWidth ? 'SolidXClose' : 'SolidChevronLeft'}
              size='2.4rem'
              color={colors.lavender[700]}
            />
          </button>
          {headerText}
        </h2>

        <Badge color='purple'>{betslipCount}</Badge>
      </div>

      {isLoggedIn ? (
        <div>
          <div>
            <BalancePill renderContext='betslip' />
          </div>

          <MyBetsButtons shouldDisplayPendingBets displayLocation='betslip' />
        </div>
      ) : (
        <div>
          <Button size='sm' color='primary' onClick={() => showLogin()}>
            <div>
              <span>Log in</span>
            </div>
          </Button>

          <Button
            size='sm'
            color='secondary_color'
            onClick={() => {
              signupTracker({
                interaction_type: 'Sign Up Clicked',
                signUpFlow: 'Standard',
                location: 'Bet Slip',
              })
              appHistory.push(AppRoutes.SignUp)
              dispatch(setBetSlipIsOpen(false))
            }}
          >
            <div>
              <span>Sign up</span>
            </div>
          </Button>
        </div>
      )}
    </HeaderPanelWrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function getWorkflowHeader(workflow: Workflow): string {
  if (workflow === 'proposed') return 'Review'
  if (workflow === 'bets-placed') return 'Bet Receipt'
  return 'Bet Slip'
}

/** Allow marketing team accounts to create shares */
const shouldAllowPendingSharesForAccount = (accountNumber: number | null): boolean => {
  return !!(accountNumber === 3637261 || accountNumber === 5009352 || accountNumber === 6837629)
}

// ======
// Styles
// ======

const HeaderPanelWrapperStyled = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  padding: `${spacing.sm} ${spacing.sm} ${spacing.sm} 0`,
  fontFamily: font.family.primary,

  [`&.${LocalConstants.CondensedHeaderClass}`]: {
    paddingBottom: 0,
  },

  '> div:nth-of-type(1)': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.smx1,

    // title
    '> h2': {
      display: 'flex',
      alignItems: 'center',
      fontWeight: font.weight.semibold,
      fontSize: font.size.xl2.fontSize,
      lineHeight: font.size.xl2.lineHeight,
      letterSpacing: font.size.xl2.letterSpacing,
      color: colors.black,
      textTransform: 'unset',
      textShadow: 'none',

      // chevron
      '> button': {
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        minWidth: '5rem',
      },
    },

    // number of bets
    '> span': {
      fontWeight: font.weight.semibold,
    },
  },

  // balance and pending
  '> div:nth-of-type(2)': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    gap: spacing.smx1,

    '> button': {
      paddingLeft: spacing.smx1,
      paddingRight: spacing.smx1,

      '> div': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,

        '> span:nth-of-type(1)': {
          fontWeight: font.weight.medium,
          fontSize: font.size.lg.fontSize,
          lineHeight: font.size.lg.lineHeight,
          letterSpacing: font.size.lg.letterSpacing,
        },

        '> span:nth-of-type(2)': {
          marginTop: '-0.2rem',
          fontWeight: font.weight.regular,
          fontSize: font.size.xs.fontSize,
          lineHeight: font.size.xs.lineHeight,
        },
      },
    },
  },
})

// =====
// Types
// =====

type Workflow = BetSlipWorkflowState['currentStatus']
